import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiEdit, FiTrash2, FiPlus, FiEye, FiDollarSign, FiClock, FiFileText } from 'react-icons/fi';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import toast from 'react-hot-toast';

const ServicesDatabase = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      console.log('Fetching services from Supabase...');
      
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Fetched data:', data);
      console.log('Number of services:', data?.length);
      
      setServices(data || []);
      
      if (data && data.length > 0) {
        toast.success(`Loaded ${data.length} services`);
      } else {
        toast.error('No services found in database. Please run the SQL script to insert services.');
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error(`Failed to load services: ${error.message}`);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serviceId, serviceName) => {
    if (!window.confirm(`Are you sure you want to delete "${serviceName}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceId);

      if (error) throw error;

      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleViewDetails = (service) => {
    setSelectedService(service);
    setShowDetailsModal(true);
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'application': 'bg-blue-100 text-blue-800',
      'job-services': 'bg-green-100 text-green-800',
      'other': 'bg-purple-100 text-purple-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'application': 'Educational Applications',
      'job-services': 'Job Services',
      'other': 'Other Services'
    };
    return labels[category] || category;
  };

  const filteredServices = services.filter(service => {
    const matchesCategory = filterCategory === 'all' || service.category === filterCategory;
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         service.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const groupedServices = filteredServices.reduce((acc, service) => {
    const category = service.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Services Database</h1>
              <p className="text-gray-600 mt-1">View and manage all services from Supabase</p>
            </div>
            <Button
              onClick={fetchServices}
              variant="outline"
              className="flex items-center"
              disabled={loading}
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="text-sm text-gray-600">Total Services</div>
              <div className="text-2xl font-bold text-gray-900">{services.length}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-600">Educational</div>
              <div className="text-2xl font-bold text-blue-600">
                {services.filter(s => s.category === 'application').length}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-600">Job Services</div>
              <div className="text-2xl font-bold text-green-600">
                {services.filter(s => s.category === 'job-services').length}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-600">Other Services</div>
              <div className="text-2xl font-bold text-purple-600">
                {services.filter(s => s.category === 'other').length}
              </div>
            </Card>
          </div>

          {/* Filters */}
          <Card className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Services
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or description..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Category
                </label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Categories</option>
                  <option value="application">Educational Applications</option>
                  <option value="job-services">Job Services</option>
                  <option value="other">Other Services</option>
                </select>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Services by Category */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading services...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.keys(groupedServices).length === 0 ? (
              <Card className="p-8 text-center">
                <div className="max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Services Found</h3>
                  {services.length === 0 ? (
                    <>
                      <p className="text-gray-600 mb-4">The services table is empty. Please insert services data.</p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                        <p className="text-sm font-medium text-blue-900 mb-2">To add services:</p>
                        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                          <li>Go to Supabase Dashboard</li>
                          <li>Open SQL Editor</li>
                          <li>Run the <code className="bg-blue-100 px-1 rounded">services_rows.sql</code> file</li>
                          <li>Refresh this page</li>
                        </ol>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-600">No services match your search criteria. Try adjusting your filters.</p>
                  )}
                </div>
              </Card>
            ) : (
              Object.entries(groupedServices).map(([category, categoryServices]) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-900">
                      {getCategoryLabel(category)}
                      <span className="ml-3 text-sm font-normal text-gray-500">
                        ({categoryServices.length} services)
                      </span>
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {categoryServices.map((service) => (
                      <Card key={service.id} className="p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {service.name}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadge(service.category)}`}>
                                {getCategoryLabel(service.category)}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                service.pricing_structure === 'detailed' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {service.pricing_structure === 'detailed' ? 'Detailed Pricing' : 'Simple Pricing'}
                              </span>
                            </div>

                            <p className="text-sm text-gray-600 mb-3">{service.description}</p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="flex items-center text-gray-600">
                                <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                                {service.processing_time || 'N/A'}
                              </div>
                              <div className="flex items-center text-gray-600">
                                <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
                                {service.required_documents?.length || 0} docs
                              </div>
                              <div className="flex items-center text-gray-600">
                                <SafeIcon icon={FiDollarSign} className="w-4 h-4 mr-2" />
                                {service.pricing_structure === 'detailed' 
                                  ? `${Object.keys(service.detailed_pricing?.online || {}).length} categories`
                                  : `₹${service.application_fee || 0}`
                                }
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {service.id}
                              </div>
                            </div>
                          </div>

                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleViewDetails(service)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <SafeIcon icon={FiEye} className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(service.id, service.name)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Service"
                            >
                              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Service Details Modal */}
      {showDetailsModal && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedService.name}</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Category:</span>
                      <span className="ml-2 text-gray-600">{getCategoryLabel(selectedService.category)}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Pricing Structure:</span>
                      <span className="ml-2 text-gray-600">{selectedService.pricing_structure}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Processing Time:</span>
                      <span className="ml-2 text-gray-600">{selectedService.processing_time}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Icon:</span>
                      <span className="ml-2 text-gray-600">{selectedService.icon}</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">Description:</span>
                    <p className="text-gray-600 mt-1">{selectedService.description}</p>
                  </div>
                </div>

                {/* Required Documents */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Required Documents</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedService.required_documents?.map((doc, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {doc}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Pricing Details */}
                {selectedService.pricing_structure === 'detailed' && selectedService.detailed_pricing && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Pricing Details</h3>
                    
                    {/* Online Pricing */}
                    {selectedService.detailed_pricing.online && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Online Service</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(selectedService.detailed_pricing.online).map(([category, pricing]) => (
                            <div key={category} className="bg-blue-50 p-3 rounded-lg">
                              <div className="font-medium text-gray-900 mb-2">{category}</div>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Exam Fee:</span>
                                  <span className="font-medium">₹{pricing.examFee}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Service Charge:</span>
                                  <span className="font-medium">₹{pricing.serviceCharge}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Bank Charges and Taxes:</span>
                                  <span className="font-medium">₹{pricing.bankCharge}</span>
                                </div>
                                <div className="flex justify-between border-t border-blue-200 pt-1 mt-1">
                                  <span className="text-gray-900 font-semibold">Total:</span>
                                  <span className="font-bold text-blue-600">₹{pricing.total}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Home Pricing */}
                    {selectedService.detailed_pricing.home && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Home Service</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(selectedService.detailed_pricing.home).map(([category, pricing]) => (
                            <div key={category} className="bg-green-50 p-3 rounded-lg">
                              <div className="font-medium text-gray-900 mb-2">{category}</div>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Exam Fee:</span>
                                  <span className="font-medium">₹{pricing.examFee}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Service Charge:</span>
                                  <span className="font-medium">₹{pricing.serviceCharge}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Bank Charges and Taxes:</span>
                                  <span className="font-medium">₹{pricing.bankCharge}</span>
                                </div>
                                <div className="flex justify-between border-t border-green-200 pt-1 mt-1">
                                  <span className="text-gray-900 font-semibold">Total:</span>
                                  <span className="font-bold text-green-600">₹{pricing.total}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Simple Pricing */}
                {selectedService.pricing_structure === 'simple' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Pricing Details</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Application Fee:</span>
                          <div className="text-lg font-bold text-gray-900">₹{selectedService.application_fee || 0}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Service Fee:</span>
                          <div className="text-lg font-bold text-gray-900">₹{selectedService.service_fee || 0}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Taxes:</span>
                          <div className="text-lg font-bold text-gray-900">{selectedService.taxes || 0}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                    <div>
                      <span className="font-medium">Created:</span> {new Date(selectedService.created_at).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Updated:</span> {new Date(selectedService.updated_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServicesDatabase;
